import React from 'react';
import classNames from 'classnames';

import { useConfiguration } from '../../../../context/configurationContext';
import { FormattedMessage } from '../../../../util/reactIntl';
import { NamedLink } from '../../../../components';

import { CATEGORY_IMAGES } from '../SectionCategories/SectionCategories';
import css from './SectionIntro.module.css';

// The "N+ classes" stat shows only once the number is worth saying (client ask: no small numbers).
const MIN_CLASS_COUNT_STAT = 50;

// "120+" style: round DOWN to the nearest 10 so the claim is always true.
const roundedDown = count => Math.floor(count / 10) * 10;

// One emoji per category: the collage labels, and the category pills on phones (photos on desktop).
// Keyed by the hosted category id (config.categoryConfiguration.categories[].id).
const CATEGORY_EMOJI = {
  'sports-and-fitness': '⚽',
  'arts-and-crafts': '🎨',
  'music-and-dance': '🎵',
  'stem-and-coding': '🔬',
  'language-learning': '🌍',
  'life-skills': '🧠',
  'team-sports': '🏀',
  'academic-tutor': '📚',
  'special-needs': '🧩',
  'mommy-and-me': '👶',
};
// Shorter pill names where the full category name doesn't fit two-across on a phone (client ask).
// Display only -- the category itself (search, cards) keeps its Console name.
const PILL_SHORT_LABELS = { 'language-learning': 'Languages' };

// The collage's categories, in layout order (top row, then bottom row), each with its icon. Uses the
// same category photos as the "Browse by category" section. A category missing from the hosted
// config is skipped.
const COLLAGE_CATEGORIES = [
  'arts-and-crafts',
  'music-and-dance',
  'sports-and-fitness',
  'stem-and-coding',
  'team-sports',
  'language-learning',
].map(id => ({ id, emoji: CATEGORY_EMOJI[id] }));

/**
 * Signed-out homepage intro band: the "all kids' classes in one place" pitch, the actions (browse
 * all classes, sign up, and -- below desktop, where the topbar has no room for it -- "Providers",
 * which jumps to the providers section further down), live stats, and -- desktop only -- a playful
 * collage of category photos around a KidHive "N categories" card.
 *
 * The collage is decorative (client ask): plain photos, not links, hidden from screen readers, and
 * not shown on phones/tablets at all. SSR-safe: static copy, bundled photos and hosted config only.
 *
 * @param {Object} props
 * @param {number|null} [props.classCount] total classes on the marketplace
 * @returns {JSX.Element}
 */
/**
 * "Browse by category": every category as a compact pill in two even columns, linking to search
 * filtered by it -- a round category photo on desktop, the category's emoji on phones.
 */
const CategoryPills = ({ categories }) =>
  categories.length > 0 ? (
    <div className={css.categories}>
      <h2 className={css.categoriesHeading}>
        <FormattedMessage id="SectionCategories.heading" />
      </h2>
      <ul className={css.pills}>
        {categories.map(cat => {
          const img = CATEGORY_IMAGES[cat.id];
          return (
            <li key={cat.id} className={css.pillItem}>
              <NamedLink
                name="SearchPage"
                to={{ search: `?pub_categoryLevel1=${encodeURIComponent(cat.id)}` }}
                className={css.pill}
              >
                {img ? (
                  <picture className={css.pillPhoto} aria-hidden="true">
                    <source srcSet={img.avif} type="image/avif" />
                    <img className={css.pillPhotoImg} src={img.jpg} alt="" decoding="async" />
                  </picture>
                ) : null}
                <span className={css.pillEmoji} aria-hidden="true">
                  {CATEGORY_EMOJI[cat.id] || '•'}
                </span>
                <span className={css.pillLabel}>{PILL_SHORT_LABELS[cat.id] || cat.name}</span>
              </NamedLink>
            </li>
          );
        })}
      </ul>
    </div>
  ) : null;

const SectionIntro = props => {
  const { classCount = null } = props;
  const config = useConfiguration();
  const categories = config.categoryConfiguration?.categories || [];
  const categoryCount = categories.length;
  const collage = COLLAGE_CATEGORIES.map(c => ({
    ...c,
    label: categories.find(cat => cat.id === c.id)?.name,
    img: CATEGORY_IMAGES[c.id],
  })).filter(c => c.label && c.img);
  const hasCollage = collage.length >= 3;
  const showClassStat = Number.isInteger(classCount) && classCount >= MIN_CLASS_COUNT_STAT;

  return (
    <section className={css.root}>
      <div className={classNames(css.inner, { [css.innerWithCollage]: hasCollage })}>
        <div className={css.copy}>
          <span className={css.eyebrow}>
            <FormattedMessage id="LandingPage.intro.eyebrow" />
          </span>
          <h1 className={css.title}>
            <FormattedMessage id="LandingPage.intro.title" />
          </h1>
          <p className={css.subtitle}>
            <FormattedMessage id="LandingPage.intro.subtitle" />
          </p>

          <div className={css.actions}>
            <NamedLink name="SearchPage" className={css.primaryAction}>
              <FormattedMessage id="LandingPage.intro.browse" />
            </NamedLink>
            {/* Sign up + Providers wrap as a pair, so Providers always sits next to Sign up. */}
            <span className={css.actionPair}>
              <NamedLink name="SignupPage" className={css.secondaryAction}>
                <FormattedMessage id="LandingPage.intro.signup" />
              </NamedLink>
              <NamedLink
                name="LandingPage"
                to={{ hash: '#for-providers' }}
                className={css.providersAction}
              >
                <FormattedMessage id="LandingPage.intro.providers" />
              </NamedLink>
            </span>
          </div>

          <ul className={css.stats}>
            {showClassStat ? (
              <li className={css.stat}>
                <strong className={css.statNumber}>{roundedDown(classCount)}+</strong>
                <FormattedMessage id="LandingPage.intro.statClasses" />
              </li>
            ) : null}
            {categoryCount > 0 ? (
              <li className={css.stat}>
                <strong className={css.statNumber}>{categoryCount}</strong>
                <FormattedMessage id="LandingPage.intro.statCategories" />
              </li>
            ) : null}
            <li className={css.stat}>
              <strong className={css.statNumber}>
                <FormattedMessage id="LandingPage.intro.statRegionValue" />
              </strong>
              <FormattedMessage id="LandingPage.intro.statRegion" />
            </li>
          </ul>

          <CategoryPills categories={categories} />
        </div>

        {hasCollage ? (
          <div className={css.collage} aria-hidden="true">
            {collage.map((c, i) => (
              <figure key={c.id} className={classNames(css.tile, css[`tile${i + 1}`])}>
                <span className={css.tape} />
                <picture>
                  <source srcSet={c.img.avif} type="image/avif" />
                  <img className={css.tileImage} src={c.img.jpg} alt="" decoding="async" />
                </picture>
                <figcaption className={css.tileLabel}>
                  {c.emoji} {c.label}
                </figcaption>
              </figure>
            ))}
            <div className={css.hub}>
              <span className={css.hubBrand}>{config.marketplaceName || 'KidHive'}</span>
              <span className={css.hubTitle}>
                <FormattedMessage
                  id="LandingPage.intro.hubTitle"
                  values={{ count: categoryCount }}
                />
              </span>
              <span className={css.hubBody}>
                <FormattedMessage id="LandingPage.intro.hubBody" />
              </span>
            </div>
          </div>
        ) : null}
      </div>
    </section>
  );
};

export default SectionIntro;
